/*Copyright (c) 2025 por Tecnologías de Información MALMUR y empresas afiliadas. Todos los derechos reservados.
Este software es información confidencial y propietaria de Copyright (c) 2025 por Tecnologías de Información MALMUR
 y sus afiliadas. ("Información Confidencial"). Usted no puede divulgar dicha Información Confidencial, 
 y solo puede usar dicha Información Confidencial de acuerdo con los términos del acuerdo de licencia que celebró con MALMUR.*/

/*Copyright (c) 2025 by Tecnologías de Información MALMUR and affiliated companies. All Rights Reserved.
This software is confidential and proprietary information of Copyright (c) 2025 by Tecnologías de Información 
MALMUR and its affiliates. ("Confidential Information"). You may not disclose such Confidential Information, 
and may only use such Confidential Information in accordance with the terms of the license agreement you entered 
into with MALMUR.*/



import { TableProps } from "interfaces";
import {
  HeaderEnum,
  InsuranceInterface,
  PatientInsuranceInterface,
  PatientInterface,
  QueryResultInterface,
} from "medicalsystem-shared";
import { FC, useEffect, useState } from "react";


import useHttp from "hooks/http.hook";
import { CoreAPI_URLs } from "constants/http.const";
import Box from "components/generals/Box";
import {
  Dialog,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import Button from "components/generals/Button";
import { useMaterialUIController } from "context/ThemeContext";
import InsuranceForm from "components/forms/InsuranceForm";
import { FormActionType } from "types/form.enum";

/**
 * Extended properties for the InsurancePatientHubTable component.
 * 
 * @interface InsurancePatientTableInterface
 * @extends {TableProps<InsurancePatientTableInterface>}
 * @property {Partial<PatientInterface>} [patientSelected] - Currently selected patient
 */
interface InsurancePatientTableInterface extends TableProps<InsurancePatientTableInterface> {
  patientSelected?: Partial<PatientInterface>;
}

/**
 * Insurance patient hub table component for viewing patient insurance relationships.
 * Read-only view of patient's insurance policies.
 * 
 * Features:
 * - Displays patient insurance relationships in native Material-UI table
 * - View insurance details in dialog
 * - Fixed height table (250px) with scrolling
 * - Auto-loads insurance relations when patient is selected
 * - Supports dark mode via theme controller
 * - Read-only mode - no editing/deleting
 * 
 * Table Columns (typical):
 * - Insurance provider name
 * - Policy ID
 * - Coverage details
 * - Status
 * - View button
 * 
 * Workflow:
 * 1. Patient selected triggers insurance loading
 * 2. API fetches all insurance relations for patient
 * 3. Relations displayed in table
 * 4. User clicks view to see insurance details in dialog
 * 5. Dialog displays full insurance information
 * 
 * @param {InsurancePatientTableInterface} props - Component properties
 * @param {Partial<PatientInterface>} props.patientSelected - Patient to show insurance for
 * 
 * @returns {JSX.Element} Read-only insurance table with view dialog
 */
const InsurancePatientHubTable: FC<InsurancePatientTableInterface> = ({
  patientSelected
}) => {
  const [_isLoading, _error, sendRequest] = useHttp();
  const [insuranceSelectList, setInsuranceSelectList] = useState<PatientInsuranceInterface[]>([]);
  const [selectedInsurance, setSelectedInsurance] = useState<PatientInsuranceInterface | null>(null);
  const { t } = useTranslation();
  const [controller] = useMaterialUIController();
  const [openDialog, setOpenDialog] = useState(false);
  const { darkMode } = controller;

  useEffect(() => {
    if (patientSelected) {
      sendRequest(
        CoreAPI_URLs.PATIENT_INSURANCE_GET_ALL,
        onGetInsuranceRelationsHandler,
        "GET",
        null,
        [{ header: HeaderEnum.PATIENT_ID, value: patientSelected.id }]
      );
    }
  }, [patientSelected]);

  const handleViewClick = (insurance: PatientInsuranceInterface) => {
  setSelectedInsurance(insurance);
  setOpenDialog(true);
};

  const onGetInsuranceRelationsHandler = (
    relationsList: QueryResultInterface<PatientInsuranceInterface>
  ) => {
    if (relationsList.results.length > 0) {
      setInsuranceSelectList(relationsList.results);
    }
  };

  return (
    <Box sx={{ width: "100%" }}>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <TableContainer component={Paper} sx={{ height: "250px" }}>
            <Table>
              <TableHead
                sx={{
                  display: "table-header-group",
                  "& .MuiTableCell-head": {
                    color: darkMode ? "#fff" : "#000",
                  },
                  "& .MuiTableRow-root": {
                    color: darkMode ? "#fff" : "#000",
                  },
                }}
              >
                <TableRow>
                  <TableCell align="center">{t("insurancePatientTable.name")}</TableCell>
                  <TableCell align="center">{t("insurancePatientTable.payerId")}</TableCell>
                  <TableCell align="center">{t("insurancePatientTable.action")}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody
                sx={{
                  display: "table-row-group",
                  "& .MuiTableCell-root": {
                    color: darkMode ? "#fff" : "#000",
                  },
                }}
              >
                {insuranceSelectList.map((relation) => (
                  <TableRow key={relation.insurance.id} sx={{ color: darkMode ? "#fff" : "#000" }}>
                    <TableCell align="center">{relation.insurance.name}</TableCell>
                    <TableCell align="center">{relation.insurance.payerId}</TableCell>
                    <TableCell align="center">
                      <Button variant="contained" color="info" onClick={() => handleViewClick(relation)}>
                        {t("insurancePatientTable.view")}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>
      </Grid>
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth>
       <InsuranceForm
          action={FormActionType.READ_ONLY}
          model={selectedInsurance?.insurance}
          onCloseAction={() => {
            setOpenDialog(false);
          }}
          isModal={true}
        />      
      </Dialog>
    </Box>
    
  );
};

export default InsurancePatientHubTable;
